import type {
	AppID,
	NubeSDKDevtoolsLog,
	NubeSDKDevtoolsLogLevel,
} from "@tiendanube/nube-sdk-internal-types";
import type { TelemetryLog, WorkerTelemetryMessage } from "./telemetry.ts";

const LOG_LEVELS: ReadonlySet<NubeSDKDevtoolsLogLevel> = new Set([
	"TRACE",
	"DEBUG",
	"INFO",
	"WARN",
	"ERROR",
	"FATAL",
]);

function toLogLevel(type: unknown): NubeSDKDevtoolsLogLevel {
	return LOG_LEVELS.has(type as NubeSDKDevtoolsLogLevel)
		? (type as NubeSDKDevtoolsLogLevel)
		: "INFO";
}

/** `TelemetryLog.startTime` is Unix nanoseconds; the panel works in ms. */
function toTimestamp(startTime: unknown): number {
	return typeof startTime === "number" && Number.isFinite(startTime)
		? Math.round(startTime / 1_000_000)
		: Date.now();
}

/**
 * Hands a log to the devtools hook, when the extension is present. A store
 * without devtools pays a single property read.
 */
function emitDevtoolsLog(log: NubeSDKDevtoolsLog): void {
	if (typeof window === "undefined") return;

	const hook = window.__NUBE_SDK_DEVTOOLS_HOOK__;
	// A hook installed by an older extension has no log channel.
	if (typeof hook?.emitLog !== "function") return;

	try {
		hook.emitLog(log);
	} catch (error) {
		// Instrumentation must never take the storefront down.
		console.error("NubeSDK devtools hook failed:", error);
	}
}

/**
 * Reports something the register did (or refused to do) on an app's behalf
 * — a navigation it rejected, a customization it refused — so the developer
 * sees it next to the app's own logs instead of only in the page console.
 *
 * Meant to sit next to the `console.*` call it mirrors, never to replace it.
 */
export function reportDevtoolsLog(
	app: AppID,
	level: NubeSDKDevtoolsLogLevel,
	source: string,
	message: string,
): void {
	emitDevtoolsLog({
		app: String(app),
		level,
		source,
		message,
		timestamp: Date.now(),
	});
}

/**
 * Mirrors a `telemetry:logs` message to the devtools hook.
 *
 * The payload comes from an app worker, so every field is normalized before
 * it reaches the panel. `app` is taken from the message envelope, which the
 * worker event listener has already matched against the worker's own id.
 */
export function reportLogToDevtools(message: WorkerTelemetryMessage): void {
	const log = (message.data ?? {}) as Partial<TelemetryLog>;

	emitDevtoolsLog({
		app: String(message.app),
		level: toLogLevel(log.type),
		source: String(log.source ?? "unknown"),
		event: typeof log.event === "string" ? log.event : undefined,
		message: String(log.message ?? ""),
		stack: typeof log.stack === "string" ? log.stack : undefined,
		timestamp: toTimestamp(log.startTime),
	});
}
